/**
 * @author      calvin@zumper.com (Calvin Yau)
 * @copyright   Copyright (c) 2020, Zumper
 * @description multifamilyTourTimePicker reducers
 */

import { SerializableDate, SerializableMap, type TourIntegrationNonEmpty } from '@zcode/bizos'
import type { IntegratedTourType } from '@zcode/enums'
import { payloadReducer } from '@zcode/reducers'
import { falseReducer, trueReducer } from '@zcode/reducers'
import type {
  MultifamilyTourTimePicker,
  TourAvailabilityWithMeta,
  TourIntegrationWithMeta,
} from '@zcode/state-type'
import equal from 'fast-deep-equal'
import { combineReducers } from 'redux'
import {
  type ExtractActions,
  type NarrowedAction,
  type Values,
  handleAction,
  handleActions,
} from 'redux-actions'
import invariant from 'tiny-invariant'
import type * as actions from '../actions/index'
import {
  DONE_SUBMITTING,
  ERROR_FETCHING_TOUR_AVAILABILITY,
  ERROR_FETCHING_TOUR_INTEGRATION,
  RECEIVE_SELECTED_DATETIME,
  RECEIVE_SELECTED_TOUR_TYPE,
  RECEIVE_TOUR_AVAILABILITY,
  RECEIVE_TOUR_INTEGRATION,
  RESET_SELECTED_DATETIME,
  START_FETCHING_TOUR_AVAILABILITY,
  START_FETCHING_TOUR_INTEGRATION,
  START_SUBMITTING,
} from '../constants/index'

type Actions = ExtractActions<Values<typeof actions>>

const newSerializableDateReducer = () => new SerializableDate()

export const multifamilyTourTimePicker = combineReducers<MultifamilyTourTimePicker, Actions>({
  selectedTime: handleActions<MultifamilyTourTimePicker['selectedTime'], Actions>(
    {
      [RECEIVE_SELECTED_DATETIME]: (state, { payload }) =>
        payload instanceof SerializableDate
          ? payload
          : payload && new SerializableDate(payload.getTime()),
      [RESET_SELECTED_DATETIME]: () => null,
    },
    null
  ),
  tourIntegration: (
    // biome-ignore lint/style/useDefaultParameterLast: state can be undefined
    state: MultifamilyTourTimePicker['tourIntegration'] | undefined = new SerializableMap(),
    action: Actions
  ) => {
    const { payload, type } = action
    switch (type) {
      case ERROR_FETCHING_TOUR_AVAILABILITY:
      case RECEIVE_TOUR_AVAILABILITY:
      case START_FETCHING_TOUR_AVAILABILITY:
        return state.with(
          payload.groupId,
          tourIntegrationWithMetaReducer(state.get(payload.groupId), action)
        )
      case ERROR_FETCHING_TOUR_INTEGRATION:
      case START_FETCHING_TOUR_INTEGRATION:
        return state.with(payload, tourIntegrationWithMetaReducer(state.get(payload), action))
      case RECEIVE_TOUR_INTEGRATION: {
        const groupId = typeof payload === 'number' ? payload : payload.integration.groupId
        return state.with(groupId, tourIntegrationWithMetaReducer(state.get(groupId), action))
      }
    }
    return state
  },
  tourType: handleAction<
    MultifamilyTourTimePicker['tourType'],
    Actions,
    typeof RECEIVE_SELECTED_TOUR_TYPE
  >(RECEIVE_SELECTED_TOUR_TYPE, payloadReducer, 1),
  submitting: handleActions(
    {
      [START_SUBMITTING]: trueReducer,
      [DONE_SUBMITTING]: falseReducer,
    },
    false
  ),
})

/**
 * Action types that can be passed to the tourIntegrationWithMetaReducer
 */
type TourIntegrationWithMetaActions = NarrowedAction<
  Actions,
  | typeof ERROR_FETCHING_TOUR_AVAILABILITY
  | typeof ERROR_FETCHING_TOUR_INTEGRATION
  | typeof RECEIVE_TOUR_AVAILABILITY
  | typeof RECEIVE_TOUR_INTEGRATION
  | typeof START_FETCHING_TOUR_AVAILABILITY
  | typeof START_FETCHING_TOUR_INTEGRATION
>

const tourIntegrationWithMetaReducer = combineReducers<
  TourIntegrationWithMeta,
  TourIntegrationWithMetaActions
>({
  // biome-ignore lint/style/useDefaultParameterLast: state can be undefined
  availability: (state = null, action) => {
    let tourType: IntegratedTourType | undefined
    let narrowedAction: TourAvailabilityWithMetaAction | undefined
    if (
      action.type === ERROR_FETCHING_TOUR_AVAILABILITY ||
      action.type === START_FETCHING_TOUR_AVAILABILITY
    ) {
      tourType = action.payload.type
      narrowedAction = action
    } else if (action.type === RECEIVE_TOUR_AVAILABILITY) {
      tourType = action.payload.availability.type
      narrowedAction = action
    } else if (action.type === RECEIVE_TOUR_INTEGRATION) {
      if (typeof action.payload === 'number') {
        return null
      }
      tourType = action.payload.availability.type
      narrowedAction = { payload: action.payload, type: action.type }
    }
    if (tourType) {
      invariant(narrowedAction, 'narrowedAction must be set if tourType is set')
      return state
        ? state.with(tourType, tourAvailabilityWithMetaReducer(state.get(tourType), narrowedAction))
        : new SerializableMap([
            [tourType, tourAvailabilityWithMetaReducer(undefined, narrowedAction)],
          ])
    }
    return state
  },
  fetchedAt: handleActions<TourIntegrationWithMeta['fetchedAt'], Actions>(
    {
      [ERROR_FETCHING_TOUR_INTEGRATION]: newSerializableDateReducer,
      [RECEIVE_TOUR_INTEGRATION]: newSerializableDateReducer,
    },
    null
  ),
  fetching: handleActions(
    {
      [ERROR_FETCHING_TOUR_INTEGRATION]: falseReducer,
      [RECEIVE_TOUR_INTEGRATION]: falseReducer,
      [START_FETCHING_TOUR_INTEGRATION]: trueReducer,
    },
    false
  ),
  integration: handleAction<
    TourIntegrationWithMeta['integration'],
    Actions,
    typeof RECEIVE_TOUR_INTEGRATION
  >(
    RECEIVE_TOUR_INTEGRATION,
    (state, { payload }) => (typeof payload === 'number' ? null : payload.integration),
    null
  ),
})

/**
 * Action types that can be passed to the tourAvailabilityWithMetaReducer
 */
type TourAvailabilityWithMetaAction =
  | NarrowedAction<
      TourIntegrationWithMetaActions,
      | typeof ERROR_FETCHING_TOUR_AVAILABILITY
      | typeof RECEIVE_TOUR_AVAILABILITY
      | typeof START_FETCHING_TOUR_AVAILABILITY
    >
  | (NarrowedAction<TourIntegrationWithMetaActions, typeof RECEIVE_TOUR_INTEGRATION> & {
      payload: TourIntegrationNonEmpty
    })

const tourAvailabilityWithMetaReducer = combineReducers<
  TourAvailabilityWithMeta,
  TourAvailabilityWithMetaAction
>({
  availableTimes: handleActions<
    TourAvailabilityWithMeta['availableTimes'],
    TourAvailabilityWithMetaAction
  >(
    {
      [RECEIVE_TOUR_AVAILABILITY]: (
        state,
        {
          payload: {
            availability: { availableTimes },
          },
        }
      ) => (equal(state, availableTimes) ? state : availableTimes),
      [RECEIVE_TOUR_INTEGRATION]: (state, { payload }) => payload.availability.availableTimes,
    },
    null
  ),
  fetchedAt: handleActions<TourAvailabilityWithMeta['fetchedAt'], TourAvailabilityWithMetaAction>(
    {
      [ERROR_FETCHING_TOUR_AVAILABILITY]: newSerializableDateReducer,
      [RECEIVE_TOUR_AVAILABILITY]: newSerializableDateReducer,
      [RECEIVE_TOUR_INTEGRATION]: newSerializableDateReducer,
    },
    null
  ),
  fetching: handleActions(
    {
      [ERROR_FETCHING_TOUR_AVAILABILITY]: falseReducer,
      [RECEIVE_TOUR_AVAILABILITY]: falseReducer,
      [START_FETCHING_TOUR_AVAILABILITY]: trueReducer,
    },
    false
  ),
})
